/**
 * Chamber of Trial: schema + validator for the `EC2_TRIAL_STAGE_3.0` Title
 * Data key (`TrialData`, see README.md's data-model table). A pure function
 * over parsed JSON — no React, no fetch — so it can run client-side for live
 * feedback in the `/trial` editor AND server-side as the write-time gate
 * (`server/index.ts`'s set-title-data route imports this module directly),
 * and so session 3's per-floor forms can reuse it unchanged.
 *
 * **The validator is the product** (see README.md "The design"). Every rule
 * here is one class of silent live breakage the game itself will never warn
 * about — `EnemySpawner` randomizes bad indices instead of erroring
 * (Defect A), and a reward list shorter than the challenge list throws for a
 * three-starring player (Defect C). Neither produces a game-side error or
 * log — this module is the only thing that will ever catch them.
 *
 * Two shapes that once read as faults are confirmed authoring idioms, and are
 * warnings, never errors: an `itemID` on a non-Item reward (inert, kept as a
 * note for what that reward stands in for), and an empty `enemyDatabase` slot
 * (a kill-budget placeholder — see `validateSpawnData`).
 *
 * Decisions baked in by the plan, do not relitigate:
 *   - `_spawnPos: -1` is legal (means "random position", `ProceedSpawn`).
 *     `_enemyIndex` has no such escape hatch — `-1` there is always wrong.
 *   - `path` uses JS index/bracket notation matching the parsed object
 *     (`stages[12].reward[1]`), not prose, so a UI can click straight to it.
 *   - Warnings never block save. Errors do.
 */

import {
  ENEMY_INDEX_MAX_VALUE,
  SPAWN_POS_MAX_VALUE,
  isEmptyEnemySlot,
  isValidEnemyIndex,
  isValidSpawnPos,
} from "@/lib/trial-catalog";
import { isValidChallengeType, isValidMonolithValue } from "@/lib/trial-defense";
import { ITEM_CATALOG } from "@/lib/item-catalog";
import { REWARD_TYPE_NAMES } from "@/types/cloud-data";

// ─── Shape ───────────────────────────────────────────────────────────────

export interface StageModifier {
  expMultiplier: number;
  goldMultiplier: number;
  damageMultiplier: number;
  hpMultiplier: number;
}

export interface MonolithValues {
  hitInvincibleState: boolean;
  activationTime: number;
  attackPotency: number;
  effectDuration: number;
}

export interface SpawnData {
  _enemyIndex: number;
  _spawnPos: number;
  _forceLevel: boolean;
  _level: number;
}

export interface TrialWave {
  spawnData: SpawnData[];
  onWaveSpawned: Record<string, unknown>;
  onWaveCleared: Record<string, unknown>;
}

export interface TrialChallenge {
  challengeType: number;
  amount: number;
}

export interface TrialReward {
  rewardType: number;
  hero: number;
  developerBuffID: string;
  itemID: string;
  recipe: number | null;
  amount: number;
}

export interface TrialStage {
  floor: number;
  defenseSystem: number;
  monolithValues: MonolithValues;
  modifier: StageModifier;
  waveData: TrialWave[];
  challenges: TrialChallenge[];
  reward: TrialReward[];
}

export interface TrialData {
  leaderboardVersion: number;
  stages: TrialStage[];
}

// ─── Issues ─────────────────────────────────────────────────────────────

export interface TrialIssue {
  severity: "error" | "warning";
  /** "stages[12].reward[1]" — clickable-precise, JS index notation. */
  path: string;
  message: string;
}

// The usual challenge-type triple every live floor uses. Deviation is a
// warning (rule 12), not an error — unusual, not wrong.
const USUAL_CHALLENGE_TYPES = [0, 1, 2];

// ─── Small type-guards ──────────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}
function isInt(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}
function isBool(v: unknown): v is boolean {
  return typeof v === "boolean";
}
function isStr(v: unknown): v is string {
  return typeof v === "string";
}

const ITEM_IDS = new Set(ITEM_CATALOG.map((it) => it.id));

/**
 * Validate parsed Title Data against the `TrialData` shape and the trial
 * business rules. Never throws — a structurally broken document produces a
 * shape error and validation of that subtree stops there, but sibling
 * subtrees are still checked so one bad floor doesn't hide issues on others.
 */
export function validateTrialData(parsed: unknown): TrialIssue[] {
  const issues: TrialIssue[] = [];

  if (!isPlainObject(parsed)) {
    issues.push({ severity: "error", path: "", message: "Root value must be a JSON object." });
    return issues;
  }

  // Rule 1 (shape): reject unknown top-level keys — a typo'd rename the
  // game will silently ignore instead of erroring on.
  const ALLOWED_ROOT_KEYS = new Set(["leaderboardVersion", "stages"]);
  for (const key of Object.keys(parsed)) {
    if (!ALLOWED_ROOT_KEYS.has(key)) {
      issues.push({
        severity: "error",
        path: key,
        message: `Unknown top-level key "${key}" — the game will silently ignore it. Did you mean to rename an existing field?`,
      });
    }
  }

  if (!isInt(parsed.leaderboardVersion)) {
    issues.push({
      severity: "error",
      path: "leaderboardVersion",
      message: "leaderboardVersion must be an integer.",
    });
  }

  if (!Array.isArray(parsed.stages)) {
    issues.push({ severity: "error", path: "stages", message: "stages must be an array." });
    return issues;
  }
  if (parsed.stages.length === 0) {
    issues.push({ severity: "error", path: "stages", message: "stages must not be empty." });
    return issues;
  }

  const seenFloors = new Map<number, number>(); // floor value -> first stage index

  parsed.stages.forEach((stageRaw, i) => {
    const p = `stages[${i}]`;
    if (!isPlainObject(stageRaw)) {
      issues.push({ severity: "error", path: p, message: "Stage must be an object." });
      return;
    }

    const ALLOWED_STAGE_KEYS = new Set([
      "floor",
      "defenseSystem",
      "monolithValues",
      "modifier",
      "waveData",
      "challenges",
      "reward",
    ]);
    for (const key of Object.keys(stageRaw)) {
      if (!ALLOWED_STAGE_KEYS.has(key)) {
        issues.push({
          severity: "error",
          path: `${p}.${key}`,
          message: `Unknown key "${key}" on a stage — the game will silently ignore it.`,
        });
      }
    }

    // ── floor ──
    if (!isInt(stageRaw.floor)) {
      issues.push({ severity: "error", path: `${p}.floor`, message: "floor must be an integer." });
    } else {
      const floor = stageRaw.floor;
      if (seenFloors.has(floor)) {
        issues.push({
          severity: "error",
          path: `${p}.floor`,
          message: `Duplicate floor ${floor} (also used by stages[${seenFloors.get(floor)}]).`,
        });
      } else {
        seenFloors.set(floor, i);
      }
    }

    // ── defenseSystem (rule 4) ──
    if (!isInt(stageRaw.defenseSystem)) {
      issues.push({
        severity: "error",
        path: `${p}.defenseSystem`,
        message: "defenseSystem must be an integer.",
      });
    } else if (!isValidMonolithValue(stageRaw.defenseSystem)) {
      issues.push({
        severity: "error",
        path: `${p}.defenseSystem`,
        message: `defenseSystem ${stageRaw.defenseSystem} is not a valid Monolith_Attack value.`,
      });
    }

    // ── monolithValues ──
    validateMonolithValues(stageRaw.monolithValues, `${p}.monolithValues`, issues);

    // ── modifier ──
    validateModifier(stageRaw.modifier, `${p}.modifier`, issues);

    // ── waveData (rule 9) ──
    validateWaveData(stageRaw.waveData, `${p}.waveData`, issues);

    // ── challenges (rules 4, 12) ──
    const challengeCount = validateChallenges(stageRaw.challenges, `${p}.challenges`, issues);

    // ── reward (rules 4, 5, 6, 7) ──
    validateRewards(stageRaw.reward, `${p}.reward`, challengeCount, issues);
  });

  // Rule 8: floor values contiguous from 1 (uniqueness already checked above).
  const floors = [...seenFloors.keys()].sort((a, b) => a - b);
  const expected = floors.map((_, idx) => idx + 1);
  const isContiguousFrom1 = floors.length > 0 && floors.every((f, idx) => f === expected[idx]);
  if (!isContiguousFrom1) {
    const missing: number[] = [];
    for (let f = 1; f <= (floors[floors.length - 1] ?? 0); f++) {
      if (!seenFloors.has(f)) missing.push(f);
    }
    issues.push({
      severity: "error",
      path: "stages",
      message:
        missing.length > 0
          ? `floor values must be contiguous from 1. Missing floor(s): ${missing.join(", ")}.`
          : "floor values must be contiguous from 1.",
    });
  }

  return issues;
}

function validateMonolithValues(v: unknown, path: string, issues: TrialIssue[]): void {
  if (!isPlainObject(v)) {
    issues.push({ severity: "error", path, message: "monolithValues must be an object." });
    return;
  }
  if (!isBool(v.hitInvincibleState)) {
    issues.push({
      severity: "error",
      path: `${path}.hitInvincibleState`,
      message: "hitInvincibleState must be a boolean.",
    });
  }
  for (const field of ["activationTime", "attackPotency", "effectDuration"] as const) {
    if (!isFiniteNumber(v[field])) {
      issues.push({ severity: "error", path: `${path}.${field}`, message: `${field} must be a number.` });
    }
  }
}

function validateModifier(v: unknown, path: string, issues: TrialIssue[]): void {
  if (!isPlainObject(v)) {
    issues.push({ severity: "error", path, message: "modifier must be an object." });
    return;
  }
  for (const field of [
    "expMultiplier",
    "goldMultiplier",
    "damageMultiplier",
    "hpMultiplier",
  ] as const) {
    if (!isFiniteNumber(v[field])) {
      issues.push({ severity: "error", path: `${path}.${field}`, message: `${field} must be a number.` });
    }
  }
  // Rule 10 (warning): hpMultiplier or damageMultiplier of 0 is legal but
  // almost certainly a mistake.
  if (isFiniteNumber(v.hpMultiplier) && v.hpMultiplier === 0) {
    issues.push({
      severity: "warning",
      path: `${path}.hpMultiplier`,
      message: "hpMultiplier is 0 — legal, but almost certainly unintended.",
    });
  }
  if (isFiniteNumber(v.damageMultiplier) && v.damageMultiplier === 0) {
    issues.push({
      severity: "warning",
      path: `${path}.damageMultiplier`,
      message: "damageMultiplier is 0 — legal, but almost certainly unintended.",
    });
  }
}

function validateWaveData(v: unknown, path: string, issues: TrialIssue[]): void {
  if (!Array.isArray(v)) {
    issues.push({ severity: "error", path, message: "waveData must be an array." });
    return;
  }
  // Rule 9: waveData non-empty — an empty wave never clears.
  if (v.length === 0) {
    issues.push({ severity: "error", path, message: "waveData must not be empty." });
    return;
  }

  v.forEach((waveRaw, wi) => {
    const wp = `${path}[${wi}]`;
    if (!isPlainObject(waveRaw)) {
      issues.push({ severity: "error", path: wp, message: "Wave must be an object." });
      return;
    }

    if (!Array.isArray(waveRaw.spawnData)) {
      issues.push({ severity: "error", path: `${wp}.spawnData`, message: "spawnData must be an array." });
    } else if (waveRaw.spawnData.length === 0) {
      // Rule 9: every wave's spawnData non-empty.
      issues.push({
        severity: "error",
        path: `${wp}.spawnData`,
        message: "spawnData must not be empty — an empty wave never clears.",
      });
    } else {
      waveRaw.spawnData.forEach((sd, si) => validateSpawnData(sd, `${wp}.spawnData[${si}]`, issues));
    }

    // Rule 13 (warning): onWaveSpawned / onWaveCleared are UnityEvents and
    // are inert in cloud JSON — anything in them is a false expectation.
    for (const field of ["onWaveSpawned", "onWaveCleared"] as const) {
      const evt = waveRaw[field];
      if (isPlainObject(evt) && Object.keys(evt).length > 0) {
        issues.push({
          severity: "warning",
          path: `${wp}.${field}`,
          message: `${field} is non-empty, but UnityEvents are inert in cloud JSON — nothing here will run.`,
        });
      } else if (evt !== undefined && !isPlainObject(evt)) {
        issues.push({
          severity: "error",
          path: `${wp}.${field}`,
          message: `${field} must be an object.`,
        });
      }
    }
  });
}

function validateSpawnData(v: unknown, path: string, issues: TrialIssue[]): void {
  if (!isPlainObject(v)) {
    issues.push({ severity: "error", path, message: "spawnData entry must be an object." });
    return;
  }

  // ── _enemyIndex (rule 2, + the empty-slot placeholder check) ──
  if (!isInt(v._enemyIndex)) {
    issues.push({ severity: "error", path: `${path}._enemyIndex`, message: "_enemyIndex must be an integer." });
  } else if (!isValidEnemyIndex(v._enemyIndex)) {
    // -1 is NOT legal here, unlike _spawnPos — see module header.
    issues.push({
      severity: "error",
      path: `${path}._enemyIndex`,
      message: `_enemyIndex ${v._enemyIndex} is out of range (0-${ENEMY_INDEX_MAX_VALUE}). The game randomizes this silently instead of erroring — this is Defect A.`,
    });
  } else if (isEmptyEnemySlot(v._enemyIndex)) {
    // In range, but the enemyDatabase slot has no prefab and no asset, so
    // ProceedSpawn resolves neither branch and spawns nothing.
    //
    // This is DELIBERATE, not a fault. The wave's kill budget is
    // `spawnData.Count - 1` (EnemySpawner.SpawnWave), and it clears when that
    // counter drops below zero. Enemies spawned at runtime by other enemies —
    // Cave Mite's mites — report their deaths through Main_Spawner.Dead() into
    // the same counter, but have no spawnData row of their own. Padding the
    // list with empty slots is how the budget is made to account for them.
    //
    // Live floor 14 is the worked example: 2x index 21 (Cave Mite (Big)) plus
    // 12x this empty slot = a budget of 14, matching 2 parents + 12 children.
    //
    // A warning, not an error, because the count still has to be right and
    // only the author knows how many children the parent spawns.
    issues.push({
      severity: "warning",
      path: `${path}._enemyIndex`,
      message: `_enemyIndex ${v._enemyIndex} is a counter placeholder — it spawns nothing, and only adds 1 to this wave's kill budget. Intentional when reserving budget for enemies spawned by other enemies; check the count matches.`,
    });
  }

  // ── _spawnPos (rule 3) ──
  if (!isInt(v._spawnPos)) {
    issues.push({ severity: "error", path: `${path}._spawnPos`, message: "_spawnPos must be an integer." });
  } else if (!isValidSpawnPos(v._spawnPos)) {
    issues.push({
      severity: "error",
      path: `${path}._spawnPos`,
      message: `_spawnPos ${v._spawnPos} is out of range (0-${SPAWN_POS_MAX_VALUE}, or -1 for random). The game randomizes this silently instead of erroring — this is Defect A.`,
    });
  }

  // ── _forceLevel / _level ──
  if (!isBool(v._forceLevel)) {
    issues.push({ severity: "error", path: `${path}._forceLevel`, message: "_forceLevel must be a boolean." });
  }
  if (!isInt(v._level)) {
    issues.push({ severity: "error", path: `${path}._level`, message: "_level must be an integer." });
  }
  // Rule 11 (warning): _forceLevel true but _level <= 0.
  if (v._forceLevel === true && isInt(v._level) && v._level <= 0) {
    issues.push({
      severity: "warning",
      path: `${path}._level`,
      message: "_forceLevel is true but _level is 0 or negative.",
    });
  }
}

/** Returns the challenge count so validateRewards can enforce rule 5. */
function validateChallenges(v: unknown, path: string, issues: TrialIssue[]): number {
  if (!Array.isArray(v)) {
    issues.push({ severity: "error", path, message: "challenges must be an array." });
    return 0;
  }

  const types: number[] = [];
  v.forEach((cRaw, ci) => {
    const cp = `${path}[${ci}]`;
    if (!isPlainObject(cRaw)) {
      issues.push({ severity: "error", path: cp, message: "Challenge entry must be an object." });
      return;
    }
    if (!isInt(cRaw.challengeType)) {
      issues.push({ severity: "error", path: `${cp}.challengeType`, message: "challengeType must be an integer." });
    } else {
      types.push(cRaw.challengeType);
      if (!isValidChallengeType(cRaw.challengeType)) {
        issues.push({
          severity: "error",
          path: `${cp}.challengeType`,
          message: `challengeType ${cRaw.challengeType} is not a valid TrialChallengeType value.`,
        });
      }
    }
    if (!isInt(cRaw.amount)) {
      issues.push({ severity: "error", path: `${cp}.amount`, message: "amount must be an integer." });
    }
  });

  // Rule 12 (warning): unusual challenge-type set.
  const isUsualTriple =
    types.length === USUAL_CHALLENGE_TYPES.length &&
    USUAL_CHALLENGE_TYPES.every((t) => types.includes(t));
  if (!isUsualTriple) {
    issues.push({
      severity: "warning",
      path,
      message:
        "challenges is not the usual [ClearStage, NoDeath, LimitedConsumable] triple — unusual, not necessarily wrong.",
    });
  }

  return v.length;
}

function validateRewards(
  v: unknown,
  path: string,
  challengeCount: number,
  issues: TrialIssue[]
): void {
  if (!Array.isArray(v)) {
    issues.push({ severity: "error", path, message: "reward must be an array." });
    return;
  }

  // Rule 5 (Defect C guard): rewards are indexed by star number
  // (TrialManager.cs:228) — a short list throws for a three-starring player.
  if (v.length < challengeCount) {
    issues.push({
      severity: "error",
      path,
      message: `reward has ${v.length} entr${v.length === 1 ? "y" : "ies"} but there are ${challengeCount} challenges — a player who clears all challenges will index past the end of reward[] and crash (Defect C).`,
    });
  }

  v.forEach((rRaw, ri) => {
    const rp = `${path}[${ri}]`;
    if (!isPlainObject(rRaw)) {
      issues.push({ severity: "error", path: rp, message: "Reward entry must be an object." });
      return;
    }

    if (!isInt(rRaw.rewardType)) {
      issues.push({ severity: "error", path: `${rp}.rewardType`, message: "rewardType must be an integer." });
    } else if (!(rRaw.rewardType in REWARD_TYPE_NAMES)) {
      issues.push({
        severity: "error",
        path: `${rp}.rewardType`,
        message: `rewardType ${rRaw.rewardType} is not a valid RewardType value.`,
      });
    }

    if (!isInt(rRaw.hero)) {
      issues.push({ severity: "error", path: `${rp}.hero`, message: "hero must be an integer." });
    }
    if (!isStr(rRaw.developerBuffID)) {
      issues.push({ severity: "error", path: `${rp}.developerBuffID`, message: "developerBuffID must be a string." });
    }
    if (!isStr(rRaw.itemID)) {
      issues.push({ severity: "error", path: `${rp}.itemID`, message: "itemID must be a string." });
    }
    if (rRaw.recipe !== null && !isInt(rRaw.recipe)) {
      issues.push({ severity: "error", path: `${rp}.recipe`, message: "recipe must be an integer or null." });
    }
    if (!isInt(rRaw.amount)) {
      issues.push({ severity: "error", path: `${rp}.amount`, message: "amount must be an integer." });
    }

    // Rules 6 & 7 only make sense once rewardType and itemID are confirmed
    // to be the right primitive types.
    if (isInt(rRaw.rewardType) && isStr(rRaw.itemID)) {
      if (rRaw.rewardType === 0) {
        // Rule 6: Item rewards must name a real, non-empty item.
        if (rRaw.itemID === "") {
          issues.push({
            severity: "error",
            path: `${rp}.itemID`,
            message: "itemID must not be empty when rewardType is Item (0).",
          });
        } else if (!ITEM_IDS.has(rRaw.itemID)) {
          issues.push({
            severity: "error",
            path: `${rp}.itemID`,
            message: `itemID "${rRaw.itemID}" is not in the item catalog.`,
          });
        }
      } else if (rRaw.itemID !== "") {
        // Rule 7: GameManager.GetReward reads itemID only on the Item branch,
        // so a value here grants nothing on its own — the player receives the
        // reward type's own payload and that is all.
        //
        // A warning, not an error. Live floor 13 carries
        // `itemID: "key_soul_selector"` on a Gold reward deliberately, as a
        // note for what that floor's gold stands in for. Only the author knows
        // whether a row means "grant this too" (wrong rewardType, a mistake)
        // or "record what this is" (fine).
        issues.push({
          severity: "warning",
          path: `${rp}.itemID`,
          message: `itemID "${rRaw.itemID}" is set, but rewardType ${rRaw.rewardType} (${
            REWARD_TYPE_NAMES[rRaw.rewardType] ?? "unknown"
          }) is not Item — GameManager.GetReward reads itemID only on the Item branch, so this grants nothing on its own. Fine as a note; add a separate Item reward row if the player is meant to receive it.`,
        });
      }
    }
  });
}
